import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";

export type ExportMode = "diff" | "full";

interface Props {
  onCancel: () => void;
  onConfirm: (mode: ExportMode) => Promise<void>;
}

export function ExportDialog({ onCancel, onConfirm }: Props) {
  const [mode, setMode] = useState<ExportMode>("diff");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(mode);
    } catch (e) {
      setErr(String((e as Error).message ?? e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-md shadow-lg w-full max-w-sm p-4 space-y-3">
        <h2 className="font-serif-title text-base text-stone-800">Exporter en PDF</h2>
        <div className="space-y-2">
          <label className="flex items-start gap-2 p-2 rounded border border-stone-200 cursor-pointer hover:bg-stone-50">
            <input
              type="radio"
              name="mode"
              value="diff"
              checked={mode === "diff"}
              onChange={() => setMode("diff")}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm text-stone-800 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Récapitulatif des changements
              </div>
              <div className="text-xs text-stone-500 mt-0.5">
                Une page paysage A4 avec ajouts, modifs, retraits en colonnes.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 p-2 rounded border border-stone-200 cursor-pointer hover:bg-stone-50">
            <input
              type="radio"
              name="mode"
              value="full"
              checked={mode === "full"}
              onChange={() => setMode("full")}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm text-stone-800 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Liste complète avec surlignages
              </div>
              <div className="text-xs text-stone-500 mt-0.5">
                Toute la liste, les changements surlignés en vert / ambre / rose.
              </div>
            </div>
          </label>
        </div>
        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        <div className="flex gap-2 pt-1">
          <button
            className="flex-1 border border-stone-300 rounded px-3 py-2 text-sm bg-white hover:bg-stone-50"
            onClick={onCancel}
            disabled={busy}
          >
            Annuler
          </button>
          <button
            className="flex-1 bg-stone-800 text-white rounded px-3 py-2 text-sm hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center gap-2"
            onClick={submit}
            disabled={busy}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Télécharger
          </button>
        </div>
      </div>
    </div>
  );
}
